(() => {
  const callback = async () => {
    const getCartItems = async () => {
      try {
        const url = `${window.Shopify.routes.root}cart.js`;
        const request = await fetch(url);
        const data = await request.json();

        return data?.items ?? [];
      } catch (error) {
        console.error(error);
      }
    };

    const fetchStorefront = async (query) => {
      const TOKEN = `6a687b6243d79bc49f32306634830cb5`;
      const url = `https://${window.Shopify.shop}/api/2024-04/graphql.json`;

      const request = await fetch(url, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': TOKEN,
          'Content-Type': 'application/graphql',
        },
        body: query,
      });
      const data = await request.json();

      return data?.data;
    };

    // Campaign only. While a free gift is unlocked but not yet in the cart, the trigger items
    // must stay in play so the module keeps recommending their matching gift product.
    const getGiftPriorityState = async ({ upsellBlock }) => {
      const { giftTriggerCollection: triggerCollection, giftCollection, giftMax } = upsellBlock?.dataset ?? {};
      if (!triggerCollection || !giftCollection) return;

      const maxGifts = Number(giftMax) || 0;

      try {
        const url = `${window.Shopify.routes.root}cart?view=itemsCollections`;
        const request = await fetch(url);
        const data = await request.json();
        const items = data?.items ?? [];

        const itemsIn = (handle) =>
          items.filter((item) => item?.productCollections?.some((collection) => collection?.handle === handle));
        const countIn = (handle) => itemsIn(handle).reduce((sum, item) => sum + item.quantity, 0);

        const triggers = countIn(triggerCollection);
        const gifts = countIn(giftCollection);

        // Same rule as the progress bar: one gift item always stays paid, and the discount stops
        // after maxGifts. A gift is waiting whenever fewer are in the cart than were earned.
        const earned = Math.max(0, Math.min(triggers, gifts - 1));
        const free = maxGifts > 0 ? Math.min(earned, maxGifts) : earned;
        const waiting = gifts > 0 && free < (maxGifts > 0 ? Math.min(triggers, maxGifts) : triggers);
        const atMax = maxGifts > 0 && free >= maxGifts;

        return {
          waiting,
          // Every gift earned so far is in the cart: the next one starts with another trigger item
          nextCycle: !waiting && !atMax && free > 0,
          triggerProductIds: itemsIn(triggerCollection).map((item) => item.productId),
          giftProductIds: itemsIn(giftCollection).map((item) => item.productId),
        };
      } catch (error) {
        console.error(error);
      }
    };

    // All product handles of the campaign gift collection, every page: used both to keep the
    // matched gifts inside the campaign and as a fallback when nothing matches.
    const getGiftCollectionHandles = async ({ giftCollection }) => {
      if (!giftCollection) return [];

      const handles = [];

      try {
        let after = null;
        let hasNextPage = true;

        while (hasNextPage) {
          const data = await fetchStorefront(`{
            collection(handle: ${JSON.stringify(giftCollection)}) {
              products(first: 250${after ? `, after: ${JSON.stringify(after)}` : ''}) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    handle
                  }
                }
              }
            }
          }`);
          const products = data?.collection?.products;

          products?.edges?.forEach((edge) => edge?.node?.handle && handles.push(edge.node.handle));
          hasNextPage = !!products?.pageInfo?.hasNextPage;
          after = products?.pageInfo?.endCursor;
        }
      } catch (error) {
        console.error(error);
      }

      return handles;
    };

    const getSearchCriteria = async ({ productHandle }) => {
      if (!productHandle) return;

      try {
        const tagPairsJson = window?.themeComponents?.snippets?.['upsell-v2-ds']?.tagPairs;
        if (!tagPairsJson) return;

        const url = `${window.Shopify.routes.root}products/${productHandle}?view=upsell-product-json`;
        const request = await fetch(url);
        const data = await request.json();
        const { tags, material } = data ?? {};

        const searchCriteria = {
          styleTag: null,
          material: null,
          includedTags: [],
          excludedTags: [],
        };

        const styleTag = tags?.find((tag) => tag.includes('style-'));
        if (styleTag) searchCriteria.styleTag = styleTag;

        if (material) searchCriteria.material = material;

        const tagSelectors = {
          '+': searchCriteria.includedTags,
          '-': searchCriteria.excludedTags,
        };

        const tagPairs = JSON.parse(tagPairsJson).split(';').filter(Boolean);

        tagPairs.forEach((pair) => {
          const [targetTag, selectionRules] = pair.split(':');

          const hasTargetTag = targetTag && tags?.includes(targetTag);
          if (!hasTargetTag) return;

          const rules = selectionRules?.split(',') ?? [];
          rules.forEach((rule) => {
            const selector = rule.charAt(0);
            const tag = rule.slice(1);

            tagSelectors[selector]?.push(tag);
          });
        });

        return searchCriteria;
      } catch (error) {
        console.error(error);
      }
    };

    const mapSearchCriteriaToArguments = ({ searchCriteria }) => {
      const { styleTag, includedTags, excludedTags } = searchCriteria ?? {};

      const hasRequiredTags = styleTag && (!!includedTags?.length || !!excludedTags?.length);
      if (!hasRequiredTags) return;

      const style = `tag:'${styleTag}'`;
      const include = includedTags.map((tag) => `tag:'${tag}'`).join(' OR ');
      const exclude = excludedTags.map((tag) => `tag_not:'${tag}'`).join(' AND ');

      const groupTerms = (value) => `(${value})`;
      const arguments = [style, include, exclude].filter(Boolean).map(groupTerms).join(' AND ');

      return arguments;
    };

    const getSameSetProducts = async ({ queryArguments }) => {
      if (!queryArguments) return;

      try {
        const data = await fetchStorefront(`{
          products(first: 100, query: "${queryArguments}") {
            edges {
              node {
                handle
                tags
                material: metafield(namespace: "my_fields", key: "material") {
                  value
                }
              }
            }
          }
        }`);

        return data?.products?.edges;
      } catch (error) {
        console.error(error);
      }
    };

    const mapProductsToArguments = ({ products }) => {
      if (!products) return;

      const productHandles = products.reduce((accumulator, current) => {
        const handle = current?.node?.handle;
        if (handle) accumulator.push('handle:' + handle);

        return accumulator;
      }, []);

      const arguments = productHandles.join(' OR ');

      return arguments;
    };

    const getUpsellContent = async ({ searchTemplate = 'upsell-cart', queryArguments }) => {
      if (!queryArguments) return;

      try {
        const url = `${window.Shopify.routes.root}search?view=${searchTemplate}&type=product&q=${queryArguments}`;
        const request = await fetch(url);
        const data = await request.text();

        return data;
      } catch (error) {
        console.error(error);
      }
    };

    const addToCart = async ({ variantId, addToCartBtn }) => {
      const payload = {
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      };

      try {
        const request = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (request.ok) {
          const data = await request.json();
          const product = data?.items?.[0];

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product,
                addToCartBtn,
              },
            }),
          );
          return;
        }

        // E.g. 422 when the last items in stock are already in the cart: same "No more in stock" note
        // as the cart's own quantity buttons, under the product that could not be added
        const actions = addToCartBtn?.closest(selectors.product)?.querySelector(selectors.actions);
        if (actions && !actions.nextElementSibling?.matches(selectors.noStock)) {
          const { locale } = window.themeComponents.snippets['upsell-v2-ds'] ?? {};
          actions.insertAdjacentHTML('afterend', `<p class="upsell-cart__no-stock">${locale?.noStock ?? ''}</p>`);
        }
      } catch (error) {
        console.error(error);
      }

      // Nothing was added, so the drawer won't rebuild and hide its preloader by itself
      document.querySelector(selectors.cartDrawerPreloader)?.setAttribute('hidden', '');
    };

    const createToggles = ({ content }) => {
      const sets = content.querySelectorAll(selectors.setWrapper);
      if (!sets) return;

      if (sets.length > 1) {
        const toggles = document.createElement('span');
        toggles.className = selectors.toggles.slice(1);

        sets.forEach((set, index) => {
          const setToggle = document.createElement('button');

          setToggle.type = 'button';
          setToggle.className = selectors.toggle.slice(1);
          setToggle.classList.toggle('active', index === 0);
          setToggle.dataset.set = set.dataset.set ?? '';
          setToggle.dataset.index = set.dataset.index ?? '';
          setToggle.title = set.dataset.set.replace('style-', '');

          toggles.appendChild(setToggle);
        });

        return toggles;
      }
    };

    const updateSliders = ({ container }) => {
      const sliders = container.querySelectorAll(selectors.slider);

      sliders.forEach((slider) => {
        if (!slider.swiper) {
          new Swiper(slider, {
            slidesPerView: 'auto',
            slidesPerGroup: 1,
            spaceBetween: 20,
            loop: true,
            mousewheel: {
              forceToAxis: true,
            },
            navigation: {
              nextEl: slider.parentNode.querySelector(selectors.sliderButtonNext),
              prevEl: slider.parentNode.querySelector(selectors.sliderButtonPrev),
            },
          });
        }
      });
    };

    const updatePrice = ({ product, option }) => {
      if (!product || !option) return;

      const { priceRegular, priceCompare } = option.dataset;

      const priceRegularElement = product.querySelector(selectors.priceRegular);
      if (priceRegularElement) priceRegularElement.innerHTML = priceRegular || '';

      const priceCompareElement = product.querySelector(selectors.priceCompare);
      if (priceCompareElement) priceCompareElement.innerHTML = priceCompare || '';
    };

    const renderContent = ({ upsellBlock, contentContainer, content }) => {
      contentContainer.innerHTML = '';

      contentContainer.appendChild(content);

      const togglesContainer = upsellBlock.querySelector(selectors.togglesContainer);
      if (togglesContainer) {
        togglesContainer.innerHTML = '';

        const togglesContent = createToggles({ content });
        if (togglesContent) togglesContainer.appendChild(togglesContent);

        togglesContainer.parentNode.classList.toggle('hidden', !togglesContent);
      }

      // Pre-select size of the already added product
      const products = contentContainer.querySelectorAll(selectors.product);
      products.forEach((product) => {
        const set = product.closest(selectors.setWrapper);
        const preselectedSize = set?.dataset?.preselectedSize;
        if (!preselectedSize) return;

        product.querySelectorAll('option').forEach((option) => {
          const isPreselectedSizeAvailable = option.innerHTML === preselectedSize && !option.hasAttribute('disabled');

          if (isPreselectedSizeAvailable) {
            option.setAttribute('selected', '');
            updatePrice({ product, option });
          }
        });
      });
    };

    const createElementFromHtml = (htmlString) => {
      const div = document.createElement('div');
      div.innerHTML = htmlString;

      return div.firstElementChild;
    };

    const togglePreloader = ({ upsellBlock, show }) => {
      if (show) upsellBlock.classList.remove('hidden');
      upsellBlock.classList.toggle('processing', show);
    };

    const onQuantityChange = ({ upsellBlock, itemsContainer, event }) => {
      // Campaign: a +/- on a quantity can unlock or consume a gift, so always rebuild
      if (upsellBlock.dataset.giftCollection) {
        initialize({ upsellBlock });
        return;
      }

      // Apps (e.g. Notify!) fire cart:updated with a plain /cart.js payload, without items_added/items_removed
      const { items, items_added: itemsAdded = [], items_removed: itemsRemoved = [] } = event?.detail?.cart || {};

      // Force reload as fallback in case the Cart JSON no longer returns the required data on Shopify's end again
      if (!itemsAdded.length && !itemsRemoved.length) {
        initialize({ upsellBlock, itemsContainer });
      }

      if (!items || !itemsRemoved.length) return;

      // Reload data only if an item is fully removed from the cart
      const isDecreasingQuantity = !!itemsRemoved.length;
      const hasMoreQuantity = items.find((item) => item.id === itemsRemoved[0].variant_id);
      if (isDecreasingQuantity && !hasMoreQuantity) {
        initialize({ upsellBlock, itemsContainer });
      }
    };

    const initialize = async ({ upsellBlock, productHandle, searchTemplate }) => {
      const contentContainer = upsellBlock.querySelector(selectors.content);
      if (!contentContainer) return;

      togglePreloader({ upsellBlock, show: true });

      const targetProducts = productHandle ? [{ handle: productHandle }] : await getCartItems();
      const giftPriorityState = productHandle ? null : await getGiftPriorityState({ upsellBlock });
      // Fetched once per initialize, shared by every gift-priority item
      const giftCollectionHandles = giftPriorityState?.waiting
        ? getGiftCollectionHandles({ giftCollection: upsellBlock.dataset.giftCollection })
        : null;

      const generatedContent = document.createElement('div');

      await Promise.all(
        targetProducts.map(async (item, index) => {
          // Skip discounted sets (Not sure if it's still needed, but left it for backward compatibility)
          const hasSetDiscount = item?.line_level_discount_allocations?.find(
            (discount) => discount?.discount_application?.title === 'Discount on set',
          );
          if (hasSetDiscount) return;

          // Skip items that are already in the set, unless this is the item that earned a gift
          // still missing from the cart — then its match is exactly what we want to recommend.
          const cartItem = document.querySelector(`${selectors.cartItem}[data-key^="${item?.id}"]`);
          const isAlreadyInSet = cartItem?.closest(selectors.cartSet);
          const isGiftPriorityItem =
            !!giftPriorityState?.waiting && giftPriorityState.triggerProductIds.includes(item?.product_id);
          // Gift taken: keep a gift item in play so the module recommends the trigger product
          // that unlocks the next one, instead of going empty.
          const isNextCycleItem =
            !!giftPriorityState?.nextCycle && giftPriorityState.giftProductIds.includes(item?.product_id);
          if (isAlreadyInSet && !isGiftPriorityItem && !isNextCycleItem) return;

          const searchCriteria = await getSearchCriteria({ productHandle: item?.handle });
          const { styleTag, material } = searchCriteria ?? {};

          const sameSetProducts = await getSameSetProducts({
            queryArguments: mapSearchCriteriaToArguments({ searchCriteria }),
          });

          // Exclude products from other sets since Shopify returns products
          // not by exact tag match, but even by partial occurance (for example, for the Black set
          // it also returns products from the Black Crystal and Black Leopard sets)
          let filteredProducts = sameSetProducts?.filter((product) => product?.node?.tags?.includes(styleTag));

          // Exclude products made of different materials if product has specified material
          const sameMaterialProducts = filteredProducts?.filter(
            (product) => product?.node?.material?.value === JSON.stringify(material),
          );

          if (!window.Shopify.designMode) {
            filteredProducts = sameMaterialProducts;
          }

          // The heading promises a free gift: only offer products the campaign discount covers
          const campaignHandles = isGiftPriorityItem ? await giftCollectionHandles : null;
          if (campaignHandles) {
            filteredProducts = filteredProducts?.filter((product) => campaignHandles.includes(product?.node?.handle));
          }

          let upsellContent = await getUpsellContent({
            queryArguments: mapProductsToArguments({ products: filteredProducts }),
            searchTemplate,
          });

          // Last resort: nothing from the same set qualifies, the promised gift must still be on screen
          if (!upsellContent && campaignHandles?.length) {
            upsellContent = await getUpsellContent({
              queryArguments: mapProductsToArguments({
                products: campaignHandles.slice(0, 12).map((handle) => ({ node: { handle } })),
              }),
              searchTemplate,
            });
          }

          if (!upsellContent) return;

          const set = createElementFromHtml(upsellContent);
          set.classList.add('hidden');
          // 'waiting' = the free gift itself, 'next' = the trigger that starts the next cycle.
          // Both must outrank the regular sets, only the first one renames the module.
          if (isGiftPriorityItem) set.dataset.giftPriority = 'waiting';
          else if (isNextCycleItem) set.dataset.giftPriority = 'next';
          set.dataset.index = index;
          set.dataset.preselectedSize = item?.options_with_values?.[0]?.value ?? '';

          // Highlight excluded products and the reason for exclusion in the theme editor
          if (window.Shopify.designMode) {
            const excludedProducts = filteredProducts.filter((product) => !sameMaterialProducts.includes(product));

            excludedProducts.forEach((product) => {
              const productItem = set.querySelector(`${selectors.product}[data-product-handle="${product.node.handle}"]`);
              if (productItem) {
                const productMaterial = JSON.parse(product.node.material?.value ?? '[]').toString();
                const content = (locale.productIsExcluded ?? '')
                  .replace(/&quot;/g, '"')
                  .replace('[material]', productMaterial)
                  .replace('[material]', material);

                productItem.setAttribute('data-before', content);
                productItem.classList.add('excluded');
              }
            });
          }

          generatedContent.appendChild(set);
        }),
      );

      const isContentEmpty = generatedContent.children.length < 1;
      upsellBlock.classList.toggle('hidden', isContentEmpty);
      if (isContentEmpty) return;

      // While the campaign has something to say — the waiting gift, or the trigger that starts
      // the next cycle — the module shows only that. Otherwise a regular set wins the first
      // slot and recommends panties while the bar asks for a bralette.
      const hasGiftSet = !!generatedContent.querySelector(':scope > [data-gift-priority]');
      if (hasGiftSet) {
        [...generatedContent.children].filter((set) => !set.dataset.giftPriority).forEach((set) => set.remove());
      }

      // Remove duplicates with the same style name
      for (const set of generatedContent.children) {
        const styleName = set.dataset.set;
        const sameSets = generatedContent.querySelectorAll(`${selectors.setWrapper} [data-set='${styleName}']`);

        if (sameSets.length > 1) set.remove();
      }

      const titleText = upsellBlock.querySelector(`${selectors.title} span`);
      const giftPriorityHeading = upsellBlock.dataset.giftHeading;
      if (titleText && giftPriorityHeading) {
        if (!titleText.dataset.defaultText) titleText.dataset.defaultText = titleText.textContent;
        const isWaitingSet = !!generatedContent.querySelector(':scope > [data-gift-priority="waiting"]');
        titleText.textContent = isWaitingSet ? giftPriorityHeading : titleText.dataset.defaultText;
      }

      const firstSet = generatedContent.firstElementChild;
      if (firstSet) firstSet.classList.remove('hidden');

      renderContent({ upsellBlock, contentContainer, content: generatedContent });
      updateSliders({ container: contentContainer });
      togglePreloader({ upsellBlock, show: false });
    };

    // Initialization
    const selectors = {
      upsell: '.upsell',
      content: '.upsell__content',
      toggles: '.upsell__toggles',
      toggle: '.upsell__toggle',
      title: '.upsell__title',
      cartSet: '#CartDrawer .cart-set',
      cartItem: '#CartDrawer .cart__item',
      setWrapper: '[data-upsell-selector="set-wrapper"]',
      togglesContainer: '[data-upsell-selector="toggles-container"]',
      slider: '[data-upsell-selector="slider"]',
      sliderButtonPrev: '[data-upsell-selector="slider-button-prev"]',
      sliderButtonNext: '[data-upsell-selector="slider-button-next"]',
      product: '[data-upsell-selector="product"]',
      priceRegular: '[data-upsell-selector="price-regular"]',
      priceCompare: '[data-upsell-selector="price-compare"]',
      sizeSelector: '[data-upsell-selector="size-selector"]',
      addToCart: '[data-upsell-selector="add-to-cart"]',
      actions: '.upsell-cart__actions, .upsell-pdp__actions',
      noStock: '.upsell-cart__no-stock',
      cartDrawerPreloader: '#CartDrawer .drawer__loading',
    };

    const { locale } = window.themeComponents.snippets['upsell-v2-ds'] ?? {};

    const upsellBlocks = document.querySelectorAll(selectors.upsell);
    upsellBlocks.forEach((upsellBlock) => {
      const { searchTemplate, productHandle } = upsellBlock.dataset;

      const acceptedTemplates = ['upsell-cart', 'upsell-pdp'];
      if (!acceptedTemplates.includes(searchTemplate)) {
        upsellBlock.remove();
        return;
      }

      const isCollapsable = upsellBlock.classList.contains('collapsable');
      if (isCollapsable) {
        const isCollapsed = sessionStorage.getItem('isUpsellCollapsed') === 'true';
        upsellBlock.classList.toggle('collapsed', isCollapsed);
      }

      if (productHandle) {
        initialize({ upsellBlock, productHandle, searchTemplate });
      } else {
        document.addEventListener('cart:markup-built', () => initialize({ upsellBlock }), {
          once: true,
        });
        document.addEventListener('cart:product-added', () => initialize({ upsellBlock }));
        document.addEventListener('cart:product-removed', () => initialize({ upsellBlock }));
        document.addEventListener('cart:updated', (event) => onQuantityChange({ upsellBlock, event }));
      }

      // Event handlers
      upsellBlock.addEventListener('click', (event) => {
        const { target } = event;

        if (target.closest(selectors.title)) {
          if (!isCollapsable) return;

          upsellBlock.classList.toggle('collapsed');

          const isCollapsed = upsellBlock.classList.contains('collapsed');
          sessionStorage.setItem('isUpsellCollapsed', isCollapsed);
        }

        if (target.matches(selectors.toggle)) {
          upsellBlock.querySelectorAll(selectors.setWrapper).forEach((set) => {
            const toggles = upsellBlock.querySelectorAll(selectors.toggle);
            toggles.forEach((toggle) => toggle.classList.toggle('active', target === toggle));

            const isRelevantSet =
              target.dataset?.set === set.dataset?.set && target.dataset?.index === set.dataset?.index;
            set.classList.toggle('hidden', !isRelevantSet);
          });
        }

        if (target.matches(selectors.addToCart)) {
          const product = target.closest(selectors.product);
          const sizeSelector = product?.querySelector(selectors.sizeSelector);
          const variantId = sizeSelector?.value;
          if (!variantId) return;

          document.dispatchEvent(new CustomEvent('cart:preloader-triggered'));
          addToCart({ variantId, addToCartBtn: target });
        }
      });

      upsellBlock.addEventListener('change', (event) => {
        const { target } = event;

        if (target.matches(selectors.sizeSelector)) {
          const product = target.closest(selectors.product);
          const option = target.querySelector(`option[value="${target.value}"]`);
          if (!product || !option) return;

          updatePrice({ product, option });
          product.querySelector(selectors.noStock)?.remove();
        }
      });
    });
  };

  window.addEventListener('DOMContentLoaded', callback);
})();
